"use client";

import { cn } from "@guru/shared/utils/cn";
import { motion, useReducedMotion } from "motion/react";

/**
 * Animated illustrations for the "Why this works" pillars — each a tiny
 * product moment in the same lit-diorama frame as the roadmap glyphs and the
 * pricing meters: a jar filling with recovered money, a clock whose work is
 * auto-done, and add-ons that snap onto a core (modular, not bundled).
 *
 * Motion is always-on but gentle; under prefers-reduced-motion every scene
 * resolves to a clean static frame. Decorative only.
 */

const INK = "var(--brand-navy)";
const SAFFRON = "var(--brand-saffron)";
const POSITIVE = "var(--brand-positive)";

export type WhyGlyphName = "recovered" | "time" | "modular";

interface SceneProps {
  reduced: boolean;
}

const loop = (reduced: boolean, duration: number, delay = 0, repeatDelay = 0) =>
  reduced
    ? { duration: 0 }
    : {
        duration,
        delay,
        repeat: Number.POSITIVE_INFINITY,
        repeatDelay,
        ease: "easeInOut" as const,
      };

/** Money recovered: coins drop into a jar whose green level keeps rising. */
function RecoveredScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Jar body */}
      <path
        d="M92 56 h56 v32 a8 8 0 0 1 -8 8 h-40 a8 8 0 0 1 -8 -8 z"
        fill="var(--card)"
        stroke={INK}
        strokeOpacity="0.25"
        strokeWidth="1.5"
      />
      <clipPath id="why-jar-clip">
        <path d="M92 56 h56 v32 a8 8 0 0 1 -8 8 h-40 a8 8 0 0 1 -8 -8 z" />
      </clipPath>
      {/* Money level rising inside the jar */}
      <motion.rect
        clipPath="url(#why-jar-clip)"
        x="92"
        width="56"
        fill={POSITIVE}
        fillOpacity="0.28"
        initial={false}
        animate={reduced ? { y: 68, height: 28 } : { y: [96, 68], height: [0, 28] }}
        transition={reduced ? { duration: 0 } : { duration: 1.4, delay: 0.3, ease: "easeOut" }}
      />
      {/* Jar rim */}
      <rect
        x="88"
        y="50"
        width="64"
        height="9"
        rx="4.5"
        fill={INK}
        fillOpacity="0.1"
        stroke={INK}
        strokeOpacity="0.25"
        strokeWidth="1.4"
      />
      {/* Coin dropping in */}
      <motion.g
        initial={false}
        animate={reduced ? { y: 0, opacity: 1 } : { y: [-26, 4], opacity: [0, 1, 1] }}
        transition={loop(reduced, 2.2, 0, 1)}
      >
        <circle cx="120" cy="42" r="11" fill={SAFFRON} />
        <text
          x="120"
          y="46.5"
          textAnchor="middle"
          fontSize="12"
          fontWeight="700"
          fill="var(--brand-saffron-contrast)"
        >
          ₹
        </text>
      </motion.g>
      {/* Recovered amount floating up */}
      <motion.text
        x="170"
        y="58"
        fontSize="13"
        fontWeight="700"
        fill={POSITIVE}
        initial={false}
        animate={reduced ? { y: 50, opacity: 1 } : { y: [58, 42], opacity: [0, 1, 0] }}
        transition={loop(reduced, 2.2, 1, 1)}
      >
        +₹
      </motion.text>
      {/* Upward arrow */}
      <motion.path
        d="M166 94 v-15 m-5 5 l5 -5 l5 5"
        fill="none"
        stroke={POSITIVE}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.3, 1, 0.3] }}
        transition={loop(reduced, 2.2, 0.6)}
      />
    </g>
  );
}

/** Time saved: the clock runs itself while tasks tick off on their own. */
function TimeScene({ reduced }: SceneProps) {
  // Round coordinates so server and client stringify identically (raw trig
  // output differs in the last float digit and trips a hydration mismatch).
  const round = (n: number) => Math.round(n * 100) / 100;
  const ticks = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    return {
      x1: round(78 + Math.sin(angle) * 28),
      y1: round(60 - Math.cos(angle) * 28),
      x2: round(78 + Math.sin(angle) * 24),
      y2: round(60 - Math.cos(angle) * 24),
      major: i % 3 === 0,
    };
  });
  const rows = [44, 60, 76];
  return (
    <g>
      {/* Clock face */}
      <circle cx="78" cy="60" r="32" fill={SAFFRON} fillOpacity="0.07" />
      <circle
        cx="78"
        cy="60"
        r="32"
        fill="none"
        stroke={INK}
        strokeOpacity="0.2"
        strokeWidth="1.6"
      />
      {ticks.map((tick) => (
        <line
          key={`${tick.x1.toFixed(1)}:${tick.y1.toFixed(1)}`}
          x1={tick.x1}
          y1={tick.y1}
          x2={tick.x2}
          y2={tick.y2}
          stroke={INK}
          strokeOpacity="0.25"
          strokeWidth={tick.major ? 2 : 1}
        />
      ))}
      {/* Hour hand */}
      <motion.line
        x1="78"
        y1="60"
        x2="78"
        y2="44"
        stroke={INK}
        strokeWidth="2.4"
        strokeLinecap="round"
        initial={false}
        animate={reduced ? { rotate: 110 } : { rotate: [0, 360] }}
        transition={
          reduced
            ? { duration: 0 }
            : { duration: 9, repeat: Number.POSITIVE_INFINITY, ease: "linear" }
        }
        style={{ transformOrigin: "78px 60px" }}
      />
      {/* Minute hand — sweeps fast: the work is flying by on its own */}
      <motion.line
        x1="78"
        y1="60"
        x2="78"
        y2="38"
        stroke={SAFFRON}
        strokeWidth="2.6"
        strokeLinecap="round"
        initial={false}
        animate={reduced ? { rotate: 230 } : { rotate: [0, 360] }}
        transition={
          reduced
            ? { duration: 0 }
            : { duration: 2.2, repeat: Number.POSITIVE_INFINITY, ease: "linear" }
        }
        style={{ transformOrigin: "78px 60px" }}
      />
      <circle cx="78" cy="60" r="3" fill={SAFFRON} />

      {/* Tasks ticking off by themselves */}
      {rows.map((y, i) => (
        <g key={y}>
          <rect x="126" y={y - 7} width="80" height="14" rx="7" fill={INK} fillOpacity="0.06" />
          <motion.g
            initial={false}
            animate={reduced ? { opacity: 1, scale: 1 } : { opacity: [0, 1], scale: [0.4, 1] }}
            transition={loop(reduced, 2.6, 0.4 + i * 0.4, 0.8)}
            style={{ transformOrigin: `135px ${y}px` }}
          >
            <circle cx="135" cy={y} r="6" fill={POSITIVE} />
            <path
              d={`M132 ${y} l2 2 l4 -4.5`}
              fill="none"
              stroke="var(--card)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.g>
          <rect x="147" y={y - 2.5} width="50" height="5" rx="2.5" fill={INK} fillOpacity="0.18" />
        </g>
      ))}
    </g>
  );
}

/** Modular, not bundled: the add-on you need snaps onto the core; others stay optional. */
function ModularScene({ reduced }: SceneProps) {
  return (
    <g>
      {/* Core block — always included */}
      <rect
        x="44"
        y="46"
        width="58"
        height="34"
        rx="8"
        fill={SAFFRON}
        fillOpacity="0.16"
        stroke={SAFFRON}
        strokeWidth="1.6"
      />
      <text
        x="73"
        y="60"
        textAnchor="middle"
        fontSize="10"
        fontWeight="700"
        fill="var(--brand-saffron-text)"
      >
        Core
      </text>
      <rect x="58" y="66" width="30" height="5" rx="2.5" fill={SAFFRON} opacity="0.5" />

      {/* Connector */}
      <text
        x="112"
        y="68"
        textAnchor="middle"
        fontSize="16"
        fontWeight="700"
        fill={INK}
        fillOpacity="0.4"
      >
        +
      </text>

      {/* Chosen add-on snapping on */}
      <motion.g
        initial={false}
        animate={reduced ? { x: 0, opacity: 1 } : { x: [16, 0], opacity: [0, 1] }}
        transition={loop(reduced, 2.6, 0.6, 1.2)}
      >
        <rect
          x="124"
          y="46"
          width="58"
          height="34"
          rx="8"
          fill="var(--card)"
          stroke={INK}
          strokeOpacity="0.4"
          strokeWidth="1.6"
        />
        <circle cx="138" cy="58" r="5.5" fill={POSITIVE} />
        <path
          d="M135.5 58 l2 2 l3.5 -4.5"
          fill="none"
          stroke="var(--card)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="148" y="55" width="26" height="5" rx="2.5" fill={INK} fillOpacity="0.25" />
        <rect x="132" y="68" width="42" height="4" rx="2" fill={INK} fillOpacity="0.14" />
      </motion.g>

      {/* Optional add-on left on the shelf — not bundled in */}
      <g opacity="0.45">
        <rect
          x="124"
          y="20"
          width="58"
          height="18"
          rx="6"
          fill="none"
          stroke={INK}
          strokeOpacity="0.4"
          strokeWidth="1.4"
          strokeDasharray="4 4"
        />
        <rect x="132" y="27" width="42" height="4" rx="2" fill={INK} fillOpacity="0.2" />
      </g>
    </g>
  );
}

const SCENES: Record<WhyGlyphName, (props: SceneProps) => React.JSX.Element> = {
  recovered: RecoveredScene,
  time: TimeScene,
  modular: ModularScene,
};

const toneFrame: Record<WhyGlyphName, string> = {
  recovered: "border-positive/25 bg-gradient-to-br from-positive/[0.08] via-card to-card",
  time: "border-brand/30 bg-gradient-to-br from-brand/[0.1] via-card to-card",
  modular: "border-ink/25 bg-gradient-to-br from-ink/[0.07] via-card to-card",
};

const toneGlow: Record<WhyGlyphName, string> = {
  recovered: "bg-positive/25",
  time: "bg-brand/35",
  modular: "bg-ink/20",
};

interface WhyGlyphProps {
  name: WhyGlyphName;
  className?: string;
}

export function WhyGlyph({ name, className }: WhyGlyphProps) {
  const reduced = !!useReducedMotion();
  const Scene = SCENES[name] ?? RecoveredScene;

  return (
    <div
      className={cn(
        "relative isolate h-28 w-full overflow-hidden rounded-xl border",
        toneFrame[name],
        className
      )}
    >
      <div className="ledger-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div
        className={cn("absolute -left-8 -top-10 h-28 w-28 rounded-full blur-2xl", toneGlow[name])}
        aria-hidden="true"
      />
      <svg
        viewBox="0 0 240 120"
        className="relative z-10 h-full w-full transition-transform duration-500 group-hover:scale-[1.04]"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <Scene reduced={reduced} />
      </svg>
    </div>
  );
}
